import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  adminAssignCourierToOrder,
  adminCreateTrackingToken,
  adminGetOrderProgress,
  adminListActiveCouriers,
} from '../utils/adminLiveTrackingAPI';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AdminLiveTrackingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, logout } = useAuth();

  const [orderCode, setOrderCode] = useState('');
  const [couriers, setCouriers] = useState([]);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trackingToken, setTrackingToken] = useState(null);
  const [trackingUrl, setTrackingUrl] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const origin = useMemo(() => {
    try {
      return window.location.origin;
    } catch (e) {
      return '';
    }
  }, []);

  async function onLoadProgress() {
    const oc = (orderCode || '').trim();
    if (!oc) {
      toast.error('Sipariş kodu gerekli');
      return;
    }

    setProgressLoading(true);
    try {
      const data = await adminGetOrderProgress(oc);
      setProgress(data);
    } catch (e) {
      console.error('🛠️ AdminLiveTrackingPage loadProgress failed', e);
      toast.error('İlerleme bilgisi alınamadı');
      setProgress(null);
    } finally {
      setProgressLoading(false);
    }
  }

  function ProgressBar({ steps }) {
    const list = Array.isArray(steps) ? steps : [];
    if (list.length === 0) return null;

    return (
      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-900">Kurye İlerleme</div>
        <div className="mt-3 grid grid-cols-1 gap-2">
          {list.map((s) => {
            const key = String(s?.key || s?.label || 'step');
            const status = String(s?.status || 'todo');
            const label = String(s?.label || key);
            const color =
              status === 'done'
                ? 'bg-green-600'
                : status === 'active'
                  ? 'bg-trendyol-orange'
                  : 'bg-gray-200';
            const textColor = status === 'todo' ? 'text-gray-600' : 'text-gray-900';
            return (
              <div key={key} className="flex items-center gap-3">
                <div className={`h-2 w-2 rounded-full ${color}`} />
                <div className={`text-sm ${textColor}`}>{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  useEffect(() => {
    console.log('🛠️ AdminLiveTrackingPage mount isLoading=', isLoading, 'isAuthenticated=', isAuthenticated);
  }, [isAuthenticated, isLoading]);

  async function onLogout() {
    console.log('🛠️ AdminLiveTrackingPage logout click');
    try {
      await logout();
      console.log('🛠️ AdminLiveTrackingPage logout ok');
      toast.success('Çıkış yapıldı');
      navigate('/giris');
    } catch (e) {
      console.error('🛠️ AdminLiveTrackingPage logout failed', e);
      toast.error('Çıkış yapılamadı');
    }
  }

  const loadCouriers = useCallback(async () => {
    console.log('🛠️ AdminLiveTrackingPage loadCouriers start');
    setLoadingCouriers(true);
    try {
      const data = await adminListActiveCouriers();
      const list = Array.isArray(data?.couriers) ? data.couriers : [];
      setCouriers(list);
      if (!selectedCourierId && list.length > 0) {
        setSelectedCourierId(String(list[0].id || ''));
      }
      console.log('🛠️ AdminLiveTrackingPage loadCouriers ok count=', list.length);
    } catch (e) {
      console.error('🛠️ AdminLiveTrackingPage loadCouriers failed', e);
      toast.error('Kuryeler yüklenemedi (admin yetkisi gerekli olabilir)');
    } finally {
      setLoadingCouriers(false);
    }
  }, [selectedCourierId]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      loadCouriers();
    }
  }, [isLoading, isAuthenticated, loadCouriers]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-600">Yükleniyor…</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-sm text-gray-700">Bu sayfayı kullanmak için giriş yapmalısın.</div>
          </div>
        </div>
      </div>
    );
  }

  async function onAssign() {
    const oc = (orderCode || '').trim();
    const cid = (selectedCourierId || '').trim();

    console.log('🛠️ AdminLiveTrackingPage onAssign click', { orderCode: oc, courierId: cid });

    if (!oc) {
      toast.error('Sipariş kodu gerekli');
      return;
    }
    if (!cid) {
      toast.error('Kurye seçmelisin');
      return;
    }

    setBusy(true);
    try {
      const res = await adminAssignCourierToOrder(oc, cid);
      console.log('🛠️ AdminLiveTrackingPage onAssign ok', res);
      toast.success('Kurye atandı');
      setTrackingToken(null);
      setTrackingUrl(null);
      setProgress(null);
    } catch (e) {
      console.error('🛠️ AdminLiveTrackingPage onAssign failed', e);
      toast.error('Kurye atanamadı');
    } finally {
      setBusy(false);
    }
  }

  async function onCreateTrackingLink() {
    const oc = (orderCode || '').trim();
    console.log('🛠️ AdminLiveTrackingPage onCreateTrackingLink click', { orderCode: oc });

    if (!oc) {
      toast.error('Sipariş kodu gerekli');
      return;
    }

    setBusy(true);
    try {
      const res = await adminCreateTrackingToken(oc);
      const token = res?.token ? String(res.token) : null;
      setTrackingToken(token);
      const url = token ? `${origin}/track/${encodeURIComponent(token)}` : null;
      setTrackingUrl(url);
      console.log('🛠️ AdminLiveTrackingPage onCreateTrackingLink ok', { token, url });
      toast.success('Takip linki oluşturuldu');
    } catch (e) {
      console.error('🛠️ AdminLiveTrackingPage onCreateTrackingLink failed', e);
      toast.error('Takip linki oluşturulamadı');
    } finally {
      setBusy(false);
    }
  }

  async function onCopyLink() {
    console.log('🛠️ AdminLiveTrackingPage onCopyLink click', { trackingUrl });
    if (!trackingUrl) return;
    try {
      await navigator.clipboard.writeText(trackingUrl);
      toast.success('Link kopyalandı');
    } catch (e) {
      toast.error('Kopyalanamadı');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-gray-900">Admin - Kurye Atama & Takip Linki</h1>
              <p className="text-sm text-gray-600 mt-1">Bu ekran admin içindir. Yetki backend tarafından kontrol edilir.</p>
            </div>
            <button
              onClick={onLogout}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
            >
              Çıkış Yap
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sipariş Kodu</label>
              <input
                value={orderCode}
                onChange={(e) => setOrderCode(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-trendyol-orange"
                placeholder="ORD-20260205-1234"
                disabled={busy}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Kurye</label>
                <button
                  onClick={loadCouriers}
                  disabled={loadingCouriers || busy}
                  className="text-xs font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  {loadingCouriers ? 'Yükleniyor…' : 'Yenile'}
                </button>
              </div>
              <select
                value={selectedCourierId}
                onChange={(e) => setSelectedCourierId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-trendyol-orange"
                disabled={busy || loadingCouriers}
              >
                {couriers.map((c) => (
                  <option key={String(c.id)} value={String(c.id)}>
                    {(c.display_name || c.phone || c.id || 'Kurye') + (c.phone ? ` (${c.phone})` : '')}
                  </option>
                ))}
                {couriers.length === 0 && <option value="">Kurye bulunamadı</option>}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onAssign}
                disabled={busy}
                className="flex-1 rounded-xl bg-gray-900 text-white py-3 font-semibold hover:opacity-95 disabled:opacity-50"
              >
                Kurye Ata
              </button>

              <button
                onClick={onCreateTrackingLink}
                disabled={busy}
                className="flex-1 rounded-xl bg-trendyol-orange text-white py-3 font-semibold hover:opacity-95 disabled:opacity-50"
              >
                Takip Linki Oluştur
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onLoadProgress}
                disabled={busy || progressLoading}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-3 font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
              >
                {progressLoading ? 'Yükleniyor…' : 'Kurye İlerlemesini Gör'}
              </button>
            </div>

            <ProgressBar steps={progress?.progress_steps} />

            {trackingUrl && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs text-gray-600">Takip Linki</div>
                <div className="mt-1 text-sm font-medium text-gray-900 break-all">{trackingUrl}</div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={onCopyLink}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-gray-50"
                  >
                    Kopyala
                  </button>
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-gray-50"
                  >
                    Aç
                  </a>
                </div>
                {trackingToken && (
                  <div className="mt-2 text-xs text-gray-500">Token: {trackingToken}</div>
                )}
              </div>
            )}

            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 text-xs text-gray-600">
              Not: Admin kontrolü backend’de `x-admin: true` + `ADMIN_EMAILS` allowlist ile yapılır.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
